import React, { FC, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  App,
  Button,
  Divider,
  Drawer,
  Empty,
  Input,
  Skeleton,
  Tabs,
} from 'antd';
import { useTheme } from 'styled-components';
import { useLoadDetails, useMovementLogs } from '@/api/TransportMovements';
import { useTranslate } from '@/translations';
import { LoadDetails, LoadLogEntry, VehicleLogEntry } from '@/types';
import {
  faAngleRight,
  faSearch,
  faXmark,
  Icon,
  VehicleInformation,
} from '@/ui/components';
import { formatCollection } from '@/modules/TransportMovement/loadList';
import { LoadStatusPill } from '@/modules/TransportMovement/components/LoadStatusPill';
import { NotRecorded } from '@/modules/TransportMovement/components/NotRecorded';
import { TransportMovementLogs } from '@/modules/TransportMovement/components/TransportMovementLogs';
import {
  Actions,
  CloseButton,
  HeaderActions,
  HeaderRoute,
  HeaderTitle,
  RowChevron,
  SectionHeader,
  SectionLabel,
  SummaryGrid,
  SummaryLabel,
  SummaryValue,
  TabCount,
  TabsContainer,
  VehicleList,
  VehicleModel,
  VehicleRow,
  VehicleStatus,
  VehicleStatusLabel,
  VehicleVin,
} from './TransportLoadDrawer.styled';
import { TransportLoadDrawerProps } from './types';

const NO_VEHICLES: LoadDetails[] = [];
const NO_LOAD_LOGS: LoadLogEntry[] = [];
const NO_VEHICLE_LOGS: VehicleLogEntry[] = [];

const openVehiclePassport = (row: HTMLElement, target: EventTarget | null) => {
  if (!(target instanceof Node) || !row.contains(target)) return;
  const label = row.querySelector<HTMLElement>('.cursor-pointer');
  if (label && !label.contains(target)) label.click();
};

export const TransportLoadDrawer: FC<TransportLoadDrawerProps> = ({
  load,
  open,
  width,
  onClose,
  afterOpenChange,
}) => {
  const { t } = useTranslate();
  const theme = useTheme();
  const { message } = App.useApp();
  const [activeTab, setActiveTab] = useState('vehicles');
  const [vinSearch, setVinSearch] = useState('');
  const loadId = open && load ? load.loadId : 0;
  const detailsQuery = useLoadDetails(loadId, { enabled: !!loadId });
  const logsQuery = useMovementLogs(loadId, { enabled: !!loadId });
  const vehicles = useMemo(
    () =>
      detailsQuery.data?.entities?.filter((vehicle) => vehicle.vin?.trim()) ??
      NO_VEHICLES,
    [detailsQuery.data],
  );
  const loadLogs = logsQuery.data?.entities?.loadLogs ?? NO_LOAD_LOGS;
  const vehicleLogs = logsQuery.data?.entities?.vehicleLogs ?? NO_VEHICLE_LOGS;
  const vehiclesLoading = detailsQuery.isFetching;
  const activityLoading = logsQuery.isFetching;
  const vehiclesError = detailsQuery.isError;
  const activityError = logsQuery.isError;

  useEffect(() => {
    setVinSearch(load?.matchedVin ?? '');
  }, [load?.loadId, load?.matchedVin]);

  const filteredVehicles = useMemo(() => {
    const term = vinSearch.trim().toLowerCase();
    return term
      ? vehicles.filter((vehicle) => vehicle.vin?.toLowerCase().includes(term))
      : vehicles;
  }, [vehicles, vinSearch]);

  const copyLoadNumber = () => {
    if (!load) return;
    navigator.clipboard
      .writeText(load.loadNumber)
      .then(() => message.success(t('tsLoadNumberCopied')))
      .catch(() => message.error(t('tsCopyFailed')));
  };

  const retryAlert = (text: string, onRetry: () => void) => (
    <Alert
      type="error"
      showIcon
      message={text}
      action={
        <Button size="small" onClick={onRetry}>
          {t('tsRetry')}
        </Button>
      }
      style={{ marginBottom: 12 }}
    />
  );

  const vehiclesTab = (
    <>
      <SectionHeader>
        <SectionLabel>{t('tsVehiclesOnLoad')}</SectionLabel>
        <Input
          allowClear
          value={vinSearch}
          onChange={(e) => setVinSearch(e.target.value)}
          placeholder={t('tsVinSearchPlaceholder')}
          aria-label={t('tsVinSearchPlaceholder')}
          prefix={<Icon icon={faSearch} />}
          disabled={vehiclesLoading || vehiclesError || vehicles.length === 0}
        />
      </SectionHeader>
      {vehiclesError &&
        retryAlert(t('tsVehiclesError'), () => detailsQuery.refetch())}
      {vehiclesLoading && <Skeleton active paragraph={{ rows: 6 }} />}
      {!vehiclesLoading && !vehiclesError && filteredVehicles.length === 0 && (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            vinSearch.trim()
              ? t('tsNoVehicleMatch', { search: vinSearch.trim() })
              : t('tsNoVehicles')
          }
        />
      )}
      {!vehiclesLoading && !vehiclesError && filteredVehicles.length > 0 && (
        <VehicleList>
          {filteredVehicles.map((vehicle) => {
            const makeModel = [vehicle.make, vehicle.model]
              .filter(Boolean)
              .join(' ');
            return (
              <VehicleRow
                key={vehicle.vin}
                tabIndex={0}
                aria-label={t('tsOpenVehicle', { vin: vehicle.vin })}
                onClick={(e) => openVehiclePassport(e.currentTarget, e.target)}
                onKeyDown={(e) => {
                  if (e.target !== e.currentTarget) return;
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.currentTarget
                      .querySelector<HTMLElement>('.cursor-pointer')
                      ?.click();
                  }
                }}
              >
                <VehicleVin>
                  <VehicleInformation vin={vehicle.vin} showAsLabel />
                </VehicleVin>
                <VehicleModel title={makeModel || undefined}>
                  {makeModel || <NotRecorded />}
                </VehicleModel>
                <VehicleStatus>
                  <VehicleStatusLabel>
                    {t('tsVehicleStatus')}
                  </VehicleStatusLabel>
                  {vehicle.vinStatus || <NotRecorded />}
                </VehicleStatus>
                <RowChevron aria-hidden>
                  <Icon icon={faAngleRight} />
                </RowChevron>
              </VehicleRow>
            );
          })}
        </VehicleList>
      )}
    </>
  );

  const activityTab = (
    <>
      {activityError &&
        retryAlert(t('tsActivityError'), () => logsQuery.refetch())}
      {activityLoading ? (
        <Skeleton active paragraph={{ rows: 6 }} />
      ) : (
        !activityError && (
          <TransportMovementLogs
            loadLogs={loadLogs}
            vehicleLogs={vehicleLogs}
          />
        )
      )}
    </>
  );

  const tabLabel = (
    label: string,
    count: number | null,
    isLoading: boolean,
  ) => (
    <>
      {label}
      {!isLoading && count !== null && <TabCount>{count}</TabCount>}
    </>
  );

  const collection = load
    ? formatCollection(load.collectionDate, {
        today: t('tsToday'),
        tomorrow: t('tsTomorrow'),
        yesterday: t('tsYesterday'),
        separator: ', ',
      })
    : null;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      afterOpenChange={afterOpenChange}
      placement="right"
      width={width}
      mask={false}
      getContainer={false}
      rootStyle={{ position: 'absolute' }}
      closable={false}
      keyboard
      aria-label={
        load ? t('tsDrawerLoad', { loadNumber: load.loadNumber }) : undefined
      }
      styles={{
        wrapper: {
          top: 16,
          right: 16,
          bottom: 16,
          borderRadius: 8,
          pointerEvents: 'auto',
        },
        content: {
          borderTop: `4px solid ${theme.accent}`,
          borderRadius: 8,
        },
        header: { padding: '20px 24px 16px', alignItems: 'flex-start' },
        body: { padding: '16px 24px 24px' },
      }}
      title={
        load && (
          <>
            <HeaderTitle>
              {t('tsDrawerLoad', { loadNumber: load.loadNumber })}
            </HeaderTitle>
            <HeaderRoute>
              {load.fromLocation || t('tsOriginNotRecorded')} →{' '}
              {load.toLocation || t('tsDestinationNotRecorded')}
            </HeaderRoute>
          </>
        )
      }
      extra={
        load && (
          <HeaderActions>
            <LoadStatusPill status={load.statusDesc} />
            <CloseButton
              type="button"
              aria-label={t('tsCloseDrawer')}
              onClick={onClose}
            >
              <Icon icon={faXmark} />
            </CloseButton>
          </HeaderActions>
        )
      }
    >
      {load && (
        <>
          <SummaryGrid>
            <div>
              <SummaryLabel>{t('tsCollection')}</SummaryLabel>
              <SummaryValue>{collection ?? <NotRecorded />}</SummaryValue>
            </div>
            <div>
              <SummaryLabel>{t('tsVehicles')}</SummaryLabel>
              <SummaryValue>
                {load.vehicleCount === null ? (
                  <NotRecorded />
                ) : (
                  t('tsVehicleCount', { count: load.vehicleCount })
                )}
              </SummaryValue>
            </div>
            <div>
              <SummaryLabel>{t('tsTransporter')}</SummaryLabel>
              <SummaryValue>
                {load.providerName || <NotRecorded />}
              </SummaryValue>
            </div>
          </SummaryGrid>

          <Actions>
            <Button size="large" onClick={copyLoadNumber}>
              {t('tsCopyLoadNumber')}
            </Button>
          </Actions>

          <Divider style={{ margin: '20px 0 4px' }} />

          <TabsContainer>
            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              items={[
                {
                  key: 'vehicles',
                  label: tabLabel(
                    t('tsVehicles'),
                    vehiclesError ? null : vehicles.length,
                    vehiclesLoading,
                  ),
                  children: vehiclesTab,
                },
                {
                  key: 'activity',
                  label: tabLabel(
                    t('tsActivity'),
                    activityError ? null : loadLogs.length + vehicleLogs.length,
                    activityLoading,
                  ),
                  children: activityTab,
                },
              ]}
            />
          </TabsContainer>
        </>
      )}
    </Drawer>
  );
};
